import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCar, FaArrowRight, FaStar, FaUsers, FaCheckCircle, FaTshirt, FaSprayCan, FaWhatsapp } from 'react-icons/fa';
import { MdLocalCarWash, MdIron } from 'react-icons/md';

const HomePage = () => {
    // Hero slider images
    const heroImages = [
        '/images/service-car-cleaning.png',
        '/images/service-steam-ironing.png',
        '/images/hero-slide-3.png',
    ];

    const [currentSlide, setCurrentSlide] = useState(0);

    // Auto-slide effect
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroImages.length);
        }, 4000); // Change slide every 4 seconds

        return () => clearInterval(interval);
    }, [heroImages.length]);



    const services = [
        {
            title: 'Daily Car Cleaning',
            description: 'Professional waterless car cleaning at your doorstep, every day.',
            image: '/images/service-car-cleaning.png',
            link: '/services#car-cleaning',
        },
        {
            title: 'Steam Ironing',
            description: 'Crisp, fresh, perfectly pressed clothes delivered to you.',
            image: '/images/service-steam-ironing.png',
            link: '/services#ironing',
        },
    ];

    const testimonials = [
        {
            name: 'Rajesh Kumar',
            role: 'Resident, Gachibowli',
            content: 'Goprezz has made my mornings so much easier. My car is always sparkling clean without any effort from my side!',
            rating: 5,
        },
        {
            name: 'Priya Sharma',
            role: 'Working Professional',
            content: 'The steam ironing service is a lifesaver. No more rushing to iron clothes before work!',
            rating: 5,
        },
        {
            name: 'Apartment Association',
            role: 'Madhapur Heights',
            content: 'We partnered with Goprezz for our entire community. Professional service and reliable staff.',
            rating: 5,
        },
    ];

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                {/* Background Image Slider - No overlays */}
                <div className="absolute inset-0">
                    {heroImages.map((image, index) => (
                        <div
                            key={index}
                            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'
                                }`}
                        >
                            <img
                                src={image}
                                alt={`Goprezz Services ${index + 1}`}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    ))}
                    {/* Dark overlay for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
                </div>

                {/* Slide Indicators */}
                <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
                    {heroImages.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide
                                ? 'bg-primary-500 w-8'
                                : 'bg-white/50 hover:bg-white/80'
                                }`}
                        />
                    ))}
                </div>

                {/* Content */}
                <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-32 text-center">
                    {/* Badge */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white backdrop-blur-sm border border-white/30 mb-8"
                    >
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                        <span className="text-gray-900 text-sm font-bold">Now Serving Hyderabad & Surrounding Areas</span>
                    </motion.div>

                    {/* Main Heading */}
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
                        style={{ color: '#ffffff', textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)' }}
                    >
                        Premium Doorstep Services
                        <br />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D] drop-shadow-md pb-2">For Modern Living</span>
                    </motion.h1>

                    {/* Subtitle */}
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="text-lg md:text-xl max-w-3xl mx-auto mb-10"
                        style={{ color: '#ffffff', textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)' }}
                    >
                        Experience the convenience of premium doorstep services for your community.
                        Professional car cleaning and steam ironing that saves your time and simplifies your daily routine.
                    </motion.p>

                    {/* CTA Buttons */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
                    >
                        <motion.a
                            href="https://wa.me/919059651065"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary text-lg"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaWhatsapp className="w-5 h-5 mr-2" />
                            Book Now via WhatsApp
                        </motion.a>
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Link to="/services" className="btn-primary text-lg">
                                Explore Services
                                <FaArrowRight className="ml-2 inline" />
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>

                {/* Scroll Indicator */}
                <motion.div
                    className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5 }}
                >
                    <motion.div
                        className="w-6 h-10 rounded-full border-2 border-white/50 flex justify-center pt-2"
                        animate={{ y: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                    >
                        <motion.div
                            className="w-1.5 h-2.5 bg-white/80 rounded-full"
                            animate={{ y: [0, 8, 0], opacity: [1, 0.3, 1] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                        />
                    </motion.div>
                </motion.div>
            </section>


            {/* Quick Services Overview */}
            <section className="section-padding bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 text-primary-600 text-sm font-semibold mb-4">
                            What We Offer
                        </span>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                            Our Premium <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D]">Services</span>
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Comprehensive doorstep solutions designed to simplify your daily life
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {services.map((service, index) => (
                            <motion.div
                                key={service.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.15 }}
                            >
                                <Link to={service.link} className="block group">
                                    <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100">
                                        {/* Image Container */}
                                        <div className="relative h-56 overflow-hidden">
                                            <img
                                                src={service.image}
                                                alt={service.title}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                                        </div>

                                        {/* Content */}
                                        <div className="p-6">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center">
                                                    {index === 0 ? (
                                                        <MdLocalCarWash className="w-7 h-7 text-primary-600" />
                                                    ) : (
                                                        <MdIron className="w-7 h-7 text-primary-600" />
                                                    )}
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-800 group-hover:text-primary-500 transition-colors">
                                                    {service.title}
                                                </h3>
                                            </div>
                                            <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>

                                            <div className="flex items-center text-primary-500 font-semibold group-hover:gap-3 transition-all duration-300">
                                                <span>Learn More</span>
                                                <FaArrowRight className="ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mt-12"
                    >
                        <Link
                            to="/services"
                            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold bg-primary-500 text-white hover:bg-primary-600 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                        >
                            View All Services
                            <FaArrowRight className="ml-2" />
                        </Link>
                    </motion.div>
                </div>
            </section>


            {/* Why Choose Us */}
            <section className="section-padding bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 text-primary-600 text-sm font-semibold mb-6">
                                Why Goprezz
                            </span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                                Making Daily Life
                                <br />
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D]">Effortlessly Simple</span>
                            </h2>
                            <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                                At Goprezz, we understand that your time is precious. That's why we've created
                                a comprehensive suite of doorstep services that handle your everyday needs with
                                professionalism and care. No more rushing around, no more compromises on quality.
                            </p>

                            <div className="space-y-4">
                                {[
                                    'Daily service at your preferred time',
                                    'Eco-friendly, waterless cleaning technology',
                                    'Trained, background-verified professionals',
                                    'Affordable monthly subscription plans',
                                    'Dedicated customer support',
                                ].map((point, index) => (
                                    <motion.div
                                        key={point}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-primary-50 transition-colors"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-primary-500/10 flex items-center justify-center">
                                            <FaCheckCircle className="w-4 h-4 text-primary-500" />
                                        </div>
                                        <span className="text-gray-700 font-medium">{point}</span>
                                    </motion.div>
                                ))}
                            </div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="mt-8"
                            >
                                <Link
                                    to="/about"
                                    className="inline-flex items-center justify-center px-6 py-3 rounded-full font-semibold border-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white transition-all duration-300"
                                >
                                    Learn More About Us
                                    <FaArrowRight className="ml-2" />
                                </Link>
                            </motion.div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                                <img
                                    src="/images/team.png"
                                    alt="Goprezz Team"
                                    className="w-full"
                                />
                            </div>
                            {/* Decorative Elements */}
                            <div className="absolute -top-4 -left-4 w-24 h-24 bg-primary-500/10 rounded-full blur-2xl"></div>
                            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-primary-500/10 rounded-full blur-2xl"></div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section-padding bg-gradient-to-r from-primary-600 to-primary-800 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('/images/hero-bg.png')] opacity-10"></div>
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-3xl md:text-5xl font-bold text-white mb-6"
                    >
                        Ready to Transform Your Daily Routine?
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-white/80 text-lg mb-8"
                    >
                        Join hundreds of happy customers who have simplified their lives with Goprezz.
                        Start your free trial today!
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="flex flex-col sm:flex-row gap-4 justify-center"
                    >
                        <a
                            href="https://wa.me/919059651065"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold bg-white text-primary-600 hover:bg-white/90 transition-all duration-300 transform hover:scale-105"
                        >
                            <FaWhatsapp className="w-5 h-5 mr-2" />
                            Contact Us Now
                        </a>
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold bg-white text-primary-600 hover:bg-white/90 transition-all duration-300 transform hover:scale-105"
                        >
                            Partner With Us
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
